import { useEffect, useState } from 'react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, MessageErreur } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { NIVEAUX_DETECTION, useSaisirDetection, type NiveauDetection } from '../../lib/useVeille';
import { CLASSE_CHAMP, CLASSE_LIBELLE, CLASSE_ZONE_TEXTE } from './format';

/**
 * Évolution relevée à la main (CDC §6.11) : Légifrance, la DREETS ou la Région
 * ne publient pas de flux que le robot puisse lire. Ce que l'équipe y relève
 * rejoint les évolutions du robot et attend la même analyse d'impact.
 */

const SOURCES_PROPOSEES = ['Légifrance', 'DREETS', 'Région Réunion', 'France Compétences', 'OPCO', 'France Travail'];

export function ModaleSaisieDetection({
  ouverte,
  onFermer,
}: {
  ouverte: boolean;
  onFermer: () => void;
}): JSX.Element | null {
  const notifier = useToast();
  const saisir = useSaisirDetection();
  const [titre, setTitre] = useState('');
  const [sourceLibelle, setSourceLibelle] = useState('');
  const [niveau, setNiveau] = useState<NiveauDetection>('IMPORTANT');
  const [survenueLe, setSurvenueLe] = useState('');
  const [reference, setReference] = useState('');
  const [lien, setLien] = useState('');
  const [detail, setDetail] = useState('');

  useEffect(() => {
    if (!ouverte) return;
    setTitre('');
    setSourceLibelle('');
    setNiveau('IMPORTANT');
    setSurvenueLe('');
    setReference('');
    setLien('');
    setDetail('');
    saisir.reset();
    // Réinitialisée à chaque ouverture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouverte]);

  function valider(): void {
    saisir.mutate(
      {
        titre,
        detail,
        sourceLibelle,
        niveau,
        reference: reference || null,
        lien: lien || null,
        survenueLe: survenueLe || null,
      },
      {
        onSuccess: () => {
          notifier('Évolution enregistrée, à analyser');
          onFermer();
        },
      },
    );
  }

  return (
    <Modale
      titre="Saisir une évolution"
      sousTitre="Texte, décret ou recommandation relevé dans une source que le robot ne lit pas"
      ouverte={ouverte}
      onFermer={onFermer}
    >
      <div className="space-y-4 p-4 sm:p-6">
        <label className="block min-w-0">
          <span className={CLASSE_LIBELLE}>Objet</span>
          <input
            value={titre}
            onChange={(evenement) => setTitre(evenement.target.value)}
            placeholder="Décret apprentissage · aide employeur"
            className={CLASSE_CHAMP}
          />
        </label>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="block min-w-0">
            <span className={CLASSE_LIBELLE}>Source</span>
            <input
              value={sourceLibelle}
              onChange={(evenement) => setSourceLibelle(evenement.target.value)}
              list="sources-veille"
              className={CLASSE_CHAMP}
            />
            <datalist id="sources-veille">
              {SOURCES_PROPOSEES.map((source) => (
                <option key={source} value={source} />
              ))}
            </datalist>
          </label>
          <label className="block min-w-0">
            <span className={CLASSE_LIBELLE}>Niveau</span>
            <select
              value={niveau}
              onChange={(evenement) => setNiveau(evenement.target.value as NiveauDetection)}
              className={CLASSE_CHAMP}
            >
              {NIVEAUX_DETECTION.map((choix) => (
                <option key={choix.valeur} value={choix.valeur}>
                  {choix.libelle}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-0">
            <span className={CLASSE_LIBELLE}>Publiée le (facultatif)</span>
            <input
              type="date"
              value={survenueLe}
              onChange={(evenement) => setSurvenueLe(evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </label>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block min-w-0">
            <span className={CLASSE_LIBELLE}>Référence (facultatif)</span>
            <input
              value={reference}
              onChange={(evenement) => setReference(evenement.target.value)}
              placeholder="RNCP37098, n° de décret…"
              className={CLASSE_CHAMP}
            />
          </label>
          <label className="block min-w-0">
            <span className={CLASSE_LIBELLE}>Lien (facultatif)</span>
            <input
              type="url"
              value={lien}
              onChange={(evenement) => setLien(evenement.target.value)}
              placeholder="https://"
              className={CLASSE_CHAMP}
            />
          </label>
        </div>

        <label className="block">
          <span className={CLASSE_LIBELLE}>Ce qui change</span>
          <textarea
            value={detail}
            onChange={(evenement) => setDetail(evenement.target.value)}
            rows={4}
            className={CLASSE_ZONE_TEXTE}
          />
        </label>

        {saisir.error instanceof ErreurApi && <MessageErreur message={saisir.error.message} />}

        <div className="flex flex-wrap justify-end gap-2">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal onClick={valider} disabled={saisir.isPending}>
            {saisir.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </BoutonPrincipal>
        </div>
      </div>
    </Modale>
  );
}
